import React, { useEffect } from "react";
import { StyleSheet, ScrollView, View, TextInput } from "react-native";
import { router, usePathname } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { RadioButtonGroup } from "@/components/ui/RadioButtonGroup";
import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons } from "@expo/vector-icons";
import { useApply } from "@/contexts/ApplyContext";

const STEP_INDEX = 3;

export default function AdditionalScreen() {
  const { colors } = useTheme();
  const { formData, updateFormData, updateStep } = useApply();
  const pathname = usePathname();

  // Track current step (only when this screen is active)
  useEffect(() => {
    if (pathname.includes("/additional")) {
      updateStep(STEP_INDEX);
    }
  }, [pathname, updateStep]);

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const allQuestionsAnswered =
    formData.heatInjury !== null &&
    formData.hasTransportation !== null &&
    formData.eventServicesExperience !== null &&
    formData.hasCellPhone !== null &&
    formData.hasAdditionalInfo !== null &&
    (formData.hasAdditionalInfo === false || formData.additionalInfo.trim() !== "");

  const handleContinue = () => {
    router.push("/apply/review");
  };

  const QuestionRow = ({
    question,
    value,
    onChange,
  }: {
    question: string;
    value: boolean | null;
    onChange: (val: boolean) => void;
  }) => (
    <View style={styles.questionRow}>
      <ThemedText style={[styles.questionText, { color: colors.text }]}>{question}</ThemedText>
      <RadioButtonGroup
        options={["Yes", "No"]}
        value={value === null ? undefined : value ? "Yes" : "No"}
        onChange={(item) => onChange(item === "Yes")}
        orientation="horizontal"
        style={styles.toggle}
      />
    </View>
  );

  return (
    <ScrollView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.content}>
        <View style={styles.header}>
          <MaterialIcons name="help-outline" size={40} color={colors.primary} />
          <ThemedText style={[styles.title, { color: colors.text }]}>Additional Questions</ThemedText>
          <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
            A few more questions to complete your application
          </ThemedText>
        </View>

        {/* Signature Section (Placeholder) */}
        <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={styles.cardHeader}>
            <MaterialIcons name="edit" size={24} color={colors.primary} />
            <ThemedText style={[styles.cardTitle, { color: colors.text }]}>Signature</ThemedText>
          </View>
          <View
            style={[
              styles.signaturePlaceholder,
              { backgroundColor: colors.backgroundSecondary, borderColor: colors.border },
            ]}
          >
            <MaterialIcons name="draw" size={32} color={colors.placeholder} />
            <ThemedText style={[styles.signaturePlaceholderText, { color: colors.placeholder }]}>
              E-signature coming soon
            </ThemedText>
          </View>
          <View style={styles.dateRow}>
            <ThemedText style={[styles.dateLabel, { color: colors.textSecondary }]}>Date:</ThemedText>
            <ThemedText style={[styles.dateValue, { color: colors.text }]}>{today}</ThemedText>
          </View>
        </View>

        {/* Questions Section */}
        <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={styles.cardHeader}>
            <MaterialIcons name="quiz" size={24} color={colors.primary} />
            <ThemedText style={[styles.cardTitle, { color: colors.text }]}>Questions</ThemedText>
          </View>

          <View style={styles.questionsList}>
            <QuestionRow
              question="Have you ever suffered from Heat Stroke, Heat Exhaustion, or other heat related injury?"
              value={formData.heatInjury}
              onChange={(val) => updateFormData({ heatInjury: val })}
            />

            <View style={[styles.divider, { backgroundColor: colors.border }]} />

            <QuestionRow
              question="Do you have your own transportation or the ability to use a driving service to get to work?"
              value={formData.hasTransportation}
              onChange={(val) => updateFormData({ hasTransportation: val })}
            />

            <View style={[styles.divider, { backgroundColor: colors.border }]} />

            <QuestionRow
              question="Have you previously been employed in the Event Services Industry, or are you currently working in this field?"
              value={formData.eventServicesExperience}
              onChange={(val) => updateFormData({ eventServicesExperience: val })}
            />

            <View style={[styles.divider, { backgroundColor: colors.border }]} />

            <QuestionRow
              question="Do you have a cell phone capable of using mobile work apps?"
              value={formData.hasCellPhone}
              onChange={(val) => updateFormData({ hasCellPhone: val })}
            />

            <View style={[styles.divider, { backgroundColor: colors.border }]} />

            <QuestionRow
              question="Do you have anything else that you'd like to tell us about yourself?"
              value={formData.hasAdditionalInfo}
              onChange={(val) => updateFormData({ hasAdditionalInfo: val, additionalInfo: "" })}
            />

            {formData.hasAdditionalInfo === true && (
              <TextInput
                style={[
                  styles.textArea,
                  {
                    backgroundColor: colors.backgroundSecondary,
                    borderColor: colors.border,
                    color: colors.text,
                  },
                ]}
                placeholder="Please add anything you'd like to mention about yourself here..."
                placeholderTextColor={colors.placeholder}
                value={formData.additionalInfo}
                onChangeText={(text) => updateFormData({ additionalInfo: text })}
                multiline
                numberOfLines={4}
                textAlignVertical="top"
              />
            )}
          </View>
        </View>

        <View style={styles.buttonRow}>
          <Button
            text="Back"
            icon="arrow-left"
            iconPosition="left"
            iconSize={18}
            iconColor={colors.text}
            onPress={() => router.push("/apply/physical")}
            style={[styles.backButton, { borderColor: colors.border }]}
            textStyle={[styles.backButtonText, { color: colors.text }]}
          />
          <Button
            text="Continue"
            icon="arrow-right"
            iconPosition="right"
            iconSize={18}
            iconColor="white"
            onPress={handleContinue}
            enabled={allQuestionsAnswered}
            style={[
              styles.continueButton,
              {
                backgroundColor: allQuestionsAnswered ? colors.primary : colors.border,
                opacity: allQuestionsAnswered ? 1 : 0.5,
              },
            ]}
            textStyle={styles.continueButtonText}
          />
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 20,
    gap: 20,
  },
  header: {
    alignItems: "center",
    gap: 12,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 16,
    textAlign: "center",
    lineHeight: 24,
  },
  card: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    gap: 16,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: "600",
  },
  signaturePlaceholder: {
    height: 100,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: "dashed",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  signaturePlaceholderText: {
    fontSize: 14,
  },
  dateRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  dateLabel: {
    fontSize: 14,
  },
  dateValue: {
    fontSize: 14,
    fontWeight: "500",
  },
  questionsList: {
    gap: 4,
  },
  questionRow: {
    gap: 10,
    paddingVertical: 8,
  },
  questionText: {
    fontSize: 15,
    lineHeight: 22,
  },
  toggle: {
    alignSelf: "flex-start",
  },
  divider: {
    height: 1,
    marginVertical: 8,
  },
  textArea: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 15,
    minHeight: 100,
    marginTop: 8,
  },
  buttonRow: {
    flexDirection: "row",
    gap: 12,
    marginTop: 8,
  },
  backButton: {
    flex: 1,
    borderRadius: 8,
    minHeight: 52,
    borderWidth: 1,
    backgroundColor: "transparent",
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: "600",
  },
  continueButton: {
    flex: 2,
    borderRadius: 8,
    minHeight: 52,
  },
  continueButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
});
